import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCircleCheck, TbAlertCircle } from "react-icons/tb";
import Popup from "src/components/Popup/Popup";
import { apiRequest } from "src/services/apiClient";
import "./cardQrPopup.scss";

const POLL_MS = 3000;
// SumUp's hosted payment page lives 30 minutes — offer a fresh code a little before that.
const EXPIRES_MS = 25 * 60 * 1000;

type Phase = "loading" | "ready" | "paid" | "expired" | "error";

interface Props {
    invoiceId: string;
    readableId: string;
    onClose: () => void;
    /** Called once when the payment is booked — the caller reloads the invoice(s). */
    onPaid: () => void;
}

/**
 * Card payment at the desk (SumUp, chapter 10): shows a QR code for the invoice's outstanding
 * amount; the patient scans it and pays on SumUp's page with their own phone. Polls the payment
 * status while open and switches to "paid" by itself.
 */
export default function CardQrPopup({ invoiceId, readableId, onClose, onPaid }: Props) {
    const { t } = useTranslation();
    const [phase, setPhase] = useState<Phase>("loading");
    const [qr, setQr] = useState<{ image: string; amount: number } | null>(null);
    const paidReported = useRef(false);

    const start = useCallback(async () => {
        setPhase("loading");
        try {
            const res = await apiRequest<{ qr: string; amount: number }>(`/sumup/invoices/${invoiceId}/desk-checkout`, { method: "POST" });
            setQr({ image: res.qr, amount: res.amount });
            setPhase("ready");
        } catch {
            setPhase("error");
        }
    }, [invoiceId]);

    useEffect(() => { void start(); }, [start]);

    useEffect(() => {
        if (phase !== "ready") return;
        const expiry = window.setTimeout(() => setPhase("expired"), EXPIRES_MS);
        const poll = window.setInterval(async () => {
            try {
                const res = await apiRequest<{ paid: boolean }>(`/sumup/invoices/${invoiceId}/desk-checkout/sync`, { method: "POST" });
                if (res.paid) setPhase("paid");
            } catch {
                // Transient — the next poll (or the webhook / safety-net cron) picks it up.
            }
        }, POLL_MS);
        return () => { window.clearTimeout(expiry); window.clearInterval(poll); };
    }, [phase, invoiceId]);

    useEffect(() => {
        if (phase !== "paid" || paidReported.current) return;
        paidReported.current = true;
        onPaid();
    }, [phase, onPaid]);

    const amount = qr ? qr.amount.toFixed(2) : "";

    return (
        <Popup
            title={t("cardQr.title", { id: readableId })}
            className="card-qr-popup"
            onClose={onClose}
            message={
                <div className="card-qr" aria-live="polite">
                    {phase === "loading" && <span className="skeleton card-qr__skeleton" />}
                    {phase === "ready" && qr && (
                        <>
                            <img className="card-qr__image" src={qr.image} alt={t("cardQr.imageAlt")} />
                            <span className="card-qr__amount">{t("cardQr.amount", { amount })}</span>
                            <p className="card-qr__hint">{t("cardQr.hint")}</p>
                            <p className="card-qr__waiting">{t("cardQr.waiting")}</p>
                        </>
                    )}
                    {phase === "paid" && (
                        <div className="card-qr__result card-qr__result--paid">
                            <TbCircleCheck />
                            <span>{t("cardQr.paid", { amount })}</span>
                        </div>
                    )}
                    {phase === "expired" && (
                        <div className="card-qr__result">
                            <TbAlertCircle />
                            <span>{t("cardQr.expired")}</span>
                        </div>
                    )}
                    {phase === "error" && (
                        <div className="card-qr__result card-qr__result--error">
                            <TbAlertCircle />
                            <span>{t("cardQr.failed")}</span>
                        </div>
                    )}
                </div>
            }
            actions={
                phase === "expired" || phase === "error"
                    ? [
                        { label: t("popup.close"), variant: "default", onClick: onClose },
                        { label: t("cardQr.newCode"), variant: "primary", onClick: () => void start() },
                    ]
                    : [{ label: t("popup.close"), variant: phase === "paid" ? "primary" : "default", onClick: onClose }]
            }
        />
    );
}
